import { useContext, type ReactNode } from 'react';
import { CardBareContext } from '@/components/ui/card-bare-context';

export interface SectionCardProps {
  /** Header row title; omit for a plain card. */
  title?: string;
  subtitle?: string;
  /** Override the subtitle color/size (defaults to the darker `text-subtext`). */
  subtitleClassName?: string;
  /** Small icon rendered in a pale-green rounded square next to the title. */
  icon?: ReactNode;
  headerRight?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** White rounded card with the shared elevation and an optional header row. */
export function SectionCard({
  title,
  subtitle,
  subtitleClassName = 'text-sm text-subtext',
  icon,
  headerRight,
  children,
  className = '',
}: SectionCardProps) {
  const bare = useContext(CardBareContext);
  if (bare) {
    // Only the outermost card goes bare; a card nested inside the section's
    // form keeps its chrome.
    return (
      <div>
        <CardBareContext.Provider value={false}>{children}</CardBareContext.Provider>
      </div>
    );
  }
  return (
    <section className={`rounded-card bg-white p-8 shadow-card ${className}`.trim()}>
      {title && (
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {icon && (
              <span
                aria-hidden="true"
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-green/10 text-dkgreen"
              >
                {icon}
              </span>
            )}
            <div>
              <h2 className="font-sans text-h5 text-navy">{title}</h2>
              {subtitle && <p className={subtitleClassName}>{subtitle}</p>}
            </div>
          </div>
          {headerRight}
        </div>
      )}
      {children}
    </section>
  );
}
